import {t} from "@lingui/core/macro";
import {Trans} from "@lingui/react/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {AnimationGroup, RoughUnderline} from "@khanacademy/khannotations";
import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import {
    markMinorConversions,
    type MinorConversionId,
} from "@khan/analytics-learning-time";
import {PageloadMarker} from "@khan/analytics-performance";
import {RequestInfo} from "@khan/shared-core";
import {staticUrl} from "@khan/urls";

import {MarketingColors} from "#marketing-content-shared-package/constants.ts";
import {fontTypes} from "#marketing-content-shared-package/typography/styles.ts";

type OwnProps = {
    kind: "HeroCondensed";
    showDistrictsCTA?: boolean;
};

type Props = OwnProps & WithActionSchedulerProps;

type AudienceButton = {
    href: string;
    conversionID: MinorConversionId;
    text: string;
};

// The mobile strategy is to target 3 break device types. Mobile, Tablet, and
// Desktop. This module is designed mobile first meaning all css rules without
// a are for mobile (<768px) devices. They then expand to tablet and desktop
// You will notice the `isOldPhone` media query. this is used very very
// sparingly, and only to fix a broken layout. We do not target old phones.
export const isOldPhone: any = "@media screen and (max-width: 374px)";
export const isTablet: any = "@media screen and (min-width: 768px)";
export const isDesktop: any = "@media screen and (min-width: 1023.5px)";

/**
 * A list of countries for which a localized hero image is available.
 *
 * This identifies the available variants of the file:
 * /images/lohp/hero_student_collage_{COUNTRY_CODE}_{DENSITY}.png
 *
 * All images are expected to be available in both 1x and 2x densities.
 *
 * An image may be localized to avoid the perception of the service
 * being overly U.S.-centric.
 */
const AVAILABLE_HERO_IMAGE_COUNTRY_CODES = {
    US: "US",
    BR: "BR",
    IN: "IN",
    PE: "PE",
} as const;

class HeroCondensed extends React.Component<Props> {
    // TODO (russell): This is hacky and not good and definitely needs to be
    // revisted at a later point. It is a temporary stop-gap until we can
    // implement a modal launcher for the "Start here" CTA on mobile/tablet.
    jumpTo: () => void = () => {
        // this ID is in ./last-chance.jsx
        const lastChanceModule = document.querySelector("#last-chance-lohp");
        if (lastChanceModule) {
            const topPosition =
                lastChanceModule.getBoundingClientRect().top +
                window.pageYOffset;
            window.scrollTo(0, topPosition);
            // @ts-expect-error - TS2339 - Property 'focus' does not exist on type 'Element'.
            lastChanceModule.focus();
        }
    };

    render(): React.ReactNode {
        const {showDistrictsCTA} = this.props;

        const mobileCopyCallout = showDistrictsCTA
            ? t`Learners, teachers, districts, and parents:`
            : t`Learners, teachers, and parents:`;

        const audienceButtons: Array<AudienceButton | null> = [
            {
                href: "/signup",
                conversionID: "logged_out_start_learning_click",
                text: t`Learners`,
            },
            {
                href: "/signup?isteacher=1",
                conversionID: "logged_out_teacher_start_click",
                text: t`Teachers`,
            },
            showDistrictsCTA
                ? {
                      href: "https://districts.khanacademy.org/",
                      conversionID: "logged_out_districts_start_click",
                      text: t`Districts`,
                  }
                : null,
            {
                href: "/signup?isparent=1",
                conversionID: "logged_out_parent_start_click",
                text: t`Parents`,
            },
        ];

        const selectedHeroImageCountryCode =
            AVAILABLE_HERO_IMAGE_COUNTRY_CODES[
                RequestInfo.remoteCountry.toUpperCase()
            ] || AVAILABLE_HERO_IMAGE_COUNTRY_CODES.US;

        // I18N: this is the copy at the top of khanacademy.org.
        return (
            <AnimationGroup>
                <View className={css(styles.heroModule)} testId="hero-heading">
                    {/* Graphic */}
                    <div className={css(styles.imgWrapper)}>
                        <img
                            className={css(styles.img)}
                            src={staticUrl(
                                `/images/lohp/hero_student_collage_${selectedHeroImageCountryCode}_2x.png`,
                            )}
                            srcSet={`${staticUrl(
                                `/images/lohp/hero_student_collage_${selectedHeroImageCountryCode}_2x.png`,
                            )} 2x, ${staticUrl(
                                `/images/lohp/hero_student_collage_${selectedHeroImageCountryCode}_1x.png`,
                            )} 1x`}
                            alt=""
                            data-testid="hero-image"
                        />
                    </div>
                    <div className={css(styles.content)}>
                        {/* Heading */}
                        <h1 className={css(styles.heading)}>
                            <Trans>
                                For every student,
                                <br />
                                every classroom.
                                <br />
                                <RoughUnderline
                                    animation={speedAnimationHero1}
                                    roughStyle={underlineStyleHero1}
                                >
                                    Real results.
                                </RoughUnderline>
                            </Trans>
                        </h1>

                        {/* Copy */}
                        <p className={css(styles.copy)}>
                            <span className={css(styles.whiteHighlightInline)}>
                                {t`We’re a nonprofit with the mission to provide a free, world-class education for anyone, anywhere.`}
                            </span>
                        </p>

                        {/* CTAs */}
                        <div className={css(styles.audienceButtonWrapper)}>
                            {audienceButtons.map(
                                (button, i) =>
                                    button && (
                                        <Button
                                            key={i}
                                            href={button.href}
                                            size="medium"
                                            safeWithNav={() =>
                                                markMinorConversions([
                                                    {
                                                        id: button.conversionID,
                                                    },
                                                ])
                                            }
                                        >
                                            {button.text}
                                        </Button>
                                    ),
                            )}
                        </div>
                        <p className={css(styles.copy, styles.mobileCallout)}>
                            {mobileCopyCallout}
                        </p>
                        <Button
                            onClick={() => {
                                markMinorConversions([
                                    {
                                        id: "logged_out_mobile_start_click",
                                    },
                                ]);
                                this.jumpTo();
                            }}
                            style={styles.startHereCTA}
                            size="medium"
                        >
                            {t`Start here`}
                        </Button>
                    </div>
                    <PageloadMarker pageName="logged_out_homepage" />
                </View>
            </AnimationGroup>
        );
    }
}

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof HeroCondensed,
        React.ComponentProps<typeof HeroCondensed>
    >
>;
export default withActionScheduler(
    HeroCondensed,
) as React.ComponentType<ExportProps>;

const heroHeadingLineHeight = {
    default: {
        default: "32px",
        isTablet: "36px",
        isDesktop: "36px",
    },
    medium: {
        default: "34px",
        isTablet: "34px",
        isDesktop: "43px",
    },
    large: {
        default: "40px",
        isTablet: "40px",
        isDesktop: "51px",
    },
} as const;

// helper function to get the correct line height
function getHeroHeadingLineHeight(mediaQuery: string) {
    const sizeMap =
        heroHeadingLineHeight[fontTypes[RequestInfo.kaLocale]] ||
        heroHeadingLineHeight["default"];
    return sizeMap[mediaQuery];
}

const speedAnimationHero1 = {
    animation: "speed",
    speed: 10,
    delay: 1200,
} as const;

const underlineStyleHero1 = {
    roughness: 0,
    stroke: MarketingColors.highlightTeal,
    strokeWidth: 2,
    bowing: {
        bowing: "inverse",
        ratio: 100,
    },
    lift: 4,
} as const;

const styles = StyleSheet.create({
    heroModule: {
        padding: "8px 20px 95px",
        color: "#21242C",
        WebkitFontSmoothing: "antialiased",
        position: "relative",
        flexDirection: "row-reverse",
        overflow: "hidden",
        maxWidth: "1152px",
        margin: "0 auto",

        [isTablet]: {
            display: "grid",
            gridTemplateColumns: "41% 1fr",
            padding: "30px 20px 10px",
            gridGap: "30px",
        },

        [isDesktop]: {
            gridTemplateColumns: "451px 1fr",
        },
    },
    content: {
        position: "absolute",
        zIndex: 10,
        width: "calc(100% - 40px)",
        bottom: 0,
        paddingBottom: "5px",

        [isOldPhone]: {
            top: 0,
            bottom: "unset",
        },

        [isTablet]: {
            position: "static",
            width: "100%",
            paddingBottom: "0px",
            alignSelf: "center",
        },
    },
    imgWrapper: {
        position: "relative",
        zIndex: 5,
        float: "right",
        width: "80%",

        [isTablet]: {
            float: "none",
            position: "static",
            width: "100%",
            alignSelf: "center",
        },
    },
    img: {
        width: "100%",
    },
    heading: {
        fontFamily: "'Source Serif Pro', 'Noto Serif', serif",
        fontSize: "24px",
        fontWeight: 600,
        lineHeight: getHeroHeadingLineHeight("default"),
        display: "inline",
        backgroundColor: color.white,
        position: "relative",
        bottom: spacing.small_12,
        [isTablet]: {
            display: "block",
            position: "static",
            fontSize: "32px",
            lineHeight: getHeroHeadingLineHeight("isTablet"),
        },
        [isDesktop]: {
            fontSize: "34px",
            lineHeight: getHeroHeadingLineHeight("isDesktop"),
        },
    },
    copy: {
        margin: "0 0 20px",
        fontWeight: 400,
        fontSize: "14px",
        lineHeight: "24px",
        width: "85%",
        maxWidth: "500px",

        [isOldPhone]: {
            width: "100%",
        },

        [isTablet]: {
            width: "100%",
            maxWidth: "100%",
            paddingRight: "15%",
        },
        [isDesktop]: {
            fontSize: "16px",
        },
    },
    mobileCallout: {
        display: "none",
    },
    audienceButtonWrapper: {
        display: "none",
        [isDesktop]: {
            display: "grid",
            gridAutoFlow: "column",
            gridGap: "10px",
        },
    },
    startHereCTA: {
        width: "100%",

        [isTablet]: {
            width: "auto",
        },
        [isDesktop]: {
            display: "none",
        },
    },
    whiteHighlightInline: {
        // Avoid clipping in ?lang=ka
        verticalAlign: "text-bottom",
        background: "white",
        display: "inline",
    },
});
